/**
 * Subscribe to a Socket.io event.
 * Handles three scenarios automatically:
 *  1. Socket already connected when component mounts → registers immediately
 *  2. Socket not yet created when component mounts (React child effects run before parent
 *     effects, so useSocket() in App.tsx may not have fired yet) → polls every 100 ms
 *  3. Socket disconnects and reconnects → re-registers on 'connect' event
 *
 * Usage:
 *   const handler = useCallback(() => { queryClient.invalidateQueries(...) }, [queryClient]);
 *   useSocketEvent('stock.updated', handler);
 */
import { useEffect, useRef } from 'react';
import { getSocket } from '@/lib/socket';
import { logger } from '@/services/logger';

export function useSocketEvent<T = Record<string, unknown>>(
  event: string,
  handler: (payload: T) => void,
): void {
  // Keep a stable ref so the effect never needs to re-run when handler identity changes
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    // Stable wrapper — always calls the latest handler without re-registering
    const invoke = (payload: T) => {
      // Log every received WS event to Loki via frontend logger
      logger.wsEvent(event, undefined, {
        payload_keys: payload && typeof payload === 'object' ? Object.keys(payload).join(',') : '',
      });
      handlerRef.current(payload);
    };

    // Re-subscribe on reconnect (covers token refresh / server restart)
    const onConnect = () => {
      const s = getSocket();
      if (!s) return;
      s.off(event, invoke);
      s.on(event, invoke);
    };

    function attach(): boolean {
      const socket = getSocket();
      if (!socket) return false;
      socket.off(event, invoke);       // prevent duplicate listeners
      socket.on(event, invoke);
      socket.off('connect', onConnect);
      socket.on('connect', onConnect);
      return true;
    }

    // Attempt immediately (works for any route change after initial load)
    const attached = attach();

    // If socket doesn't exist yet (child effect ran before App's useSocket effect),
    // poll until it becomes available — usually resolves in 1-2 ticks (~100-200 ms).
    let retryId: ReturnType<typeof setInterval> | null = null;
    let stopId: ReturnType<typeof setTimeout> | null = null;

    if (!attached) {
      retryId = setInterval(() => {
        if (attach()) {
          clearInterval(retryId!);
          if (stopId) clearTimeout(stopId);
          retryId = null;
          stopId = null;
        }
      }, 100);
      // Hard stop after 15 s to avoid leaks on auth errors
      stopId = setTimeout(() => {
        if (retryId) { clearInterval(retryId); retryId = null; }
      }, 15_000);
    }

    return () => {
      if (retryId) clearInterval(retryId);
      if (stopId) clearTimeout(stopId);
      const s = getSocket();
      if (s) {
        s.off(event, invoke);
        s.off('connect', onConnect);
      }
    };
  }, [event]); // handler is stable via ref; event name is the only real dep
}


